import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  HostListener,
  Output
} from "@angular/core";
import { Observable } from "rxjs";
import { InternalSingleNoteState, NoteStatus } from "../../../actions/@base/models";
import { UtilitiesService } from "../../../actions/@base/services";
import { SingleNote } from "@textbook-api-client/lib/interface/single-note.model";
import { DrawerTextUtil } from "../../../utils/drawer-text.util";

export enum FontSize {
  DEFAULT = 18,
  MINIMUM = 12,
  MAXIMUM = 50,
}

export interface DrawerTextConfig {
  boundsScale?: number;
  noteStatus?: NoteStatus;
  fontSize?: FontSize;
  textColor?: string|null;
  currentClickedTarget$: Observable<HTMLElement|null>;
  currentZoom$: Observable<number|null>;
  isOnline$: Observable<boolean>;
  noteId: string;
  content: string;
  supportsTouchDevices: boolean|null;
  isVirtualKeyboardSupported: boolean|null;
  isBoxActive: boolean;
  x: string;
  y: string;
  disabled: boolean;
};

@Component({
  template: '',
  selector: 'abstract-drawer-text',
  standalone: false,
})
export class AbstractDrawerTextComponent implements AfterViewInit {
  textContent!: ElementRef;
  dragContainer!: ElementRef;

  @Output() readonly textContentChange = new EventEmitter<string>();
  @Output() readonly fontSizeChange = new EventEmitter<number>();
  @Output() readonly isTextToolActive = new EventEmitter<boolean>();
  @Output() readonly saveNote = new EventEmitter<InternalSingleNoteState>();
  @Output() readonly updateNote = new EventEmitter<InternalSingleNoteState>();
  @Output() readonly removeNote = new EventEmitter<string>();
  @Output() readonly mouseEnter = new EventEmitter<void>();
  @Output() readonly mouseLeave = new EventEmitter<void>();
  @Output() readonly canvasEdited = new EventEmitter<boolean>();
  @Output() readonly hideUnClickedNotes = new EventEmitter<string>();
  @Output() readonly isActive = new EventEmitter<boolean>();
  @HostBinding('style.--x') x!: string;
  @HostBinding('style.--y') y!: string;
  @HostBinding('class.is-active') isBoxActive = false;
  @HostBinding('class.is-dragging') isDragging = false;
  @HostBinding('class.pin-position-right') isPinPositionRight = false;

  @HostListener('window:resize', ['$event'])
  onResize(event: Event): void {
    event.preventDefault();
  }

  @HostListener('mouseenter') onMouseEnter(): void {
    this.mouseEnter.next();
  }

  @HostListener('mouseleave') onMouseLeave(): void {
    this.mouseLeave.next();
  }

  @HostListener('touchend') onTouchEnd(): void {
    this.mouseLeave.next();
  }

  readonly maxLengthText = 500;
  protected readonly pinSize = '24';

  isOnline$!: Observable<boolean>;
  content!: string;
  fontSize: number = FontSize.DEFAULT;
  textColor: string|null = '#000000';
  tooltipWidth = '170';
  tooltipLeft = `-${ this.tooltipWidth }`;
  disabled!: boolean;
  protected boundsScale = 0.96;
  protected currentClickedTarget$!: Observable<HTMLElement|null>;
  protected currentZoom$!: Observable<number|null>;
  protected noteId!: string;
  protected noteStatus: NoteStatus = NoteStatus.ReadyForSave;
  protected supportsTouchDevices!: boolean|null;
  protected isVirtualKeyboardSupported!: boolean|null;
  protected caretIndex: number = 0;
  protected wrapper!: HTMLElement;

  get contentIsValid(): boolean {
    return this.content.length <= this.maxLengthText && this.content.length > 0;
  }

  constructor(
    public elementRef: ElementRef,
    protected readonly changeDetector: ChangeDetectorRef,
    protected readonly utilitiesService: UtilitiesService) {
  }

  ngAfterViewInit(): void {
    this.wrapper = this.elementRef.nativeElement.parentNode;
  }


  initWith(config: DrawerTextConfig): void {
    config.boundsScale !== undefined && (this.boundsScale = config.boundsScale);
    config.noteStatus !== undefined && (this.noteStatus = config.noteStatus);
    config.fontSize !== undefined && (this.fontSize = config.fontSize);
    config.textColor !== undefined && (this.textColor = config.textColor);
    this.currentClickedTarget$ = config.currentClickedTarget$;
    this.currentZoom$ = config.currentZoom$;
    this.isOnline$ = config.isOnline$;
    this.noteId = config.noteId;
    this.content = config.content;
    this.supportsTouchDevices = config.supportsTouchDevices;
    this.isVirtualKeyboardSupported = config.isVirtualKeyboardSupported;
    this.isBoxActive = config.isBoxActive;
    this.x = config.x;
    this.y = config.y;
  }

  setContent(content: string): void {
    this.content = content
  }

  setNoteId(id: string): void {
    this.noteId = id
  }

  getNoteId(): string {
    return this.noteId;
  }

  setNoteStatus(status: NoteStatus): void {
    this.noteStatus = status
  }

  getNoteStatus(): NoteStatus {
    return this.noteStatus;
  }

  setFontSize(size: number): void {
    this.fontSize = size
  }

  setBoxActiveState(active: boolean): void {
    this.isBoxActive = active;
  }

  setDisabled(disabled: boolean): void {
    this.disabled = disabled;
  }

  protected transformBoxContentPosition(): void {
    const {
      width: wrapperWidth,
      height: wrapperHeight,
      left: wrapperLeft,
    } = this.wrapper.getBoundingClientRect();
    const { left } = this.dragContainer?.nativeElement.getBoundingClientRect();

    this.tooltipLeft =
      DrawerTextUtil.generateTooltipLeftPositionCSS(this.tooltipWidth, this.pinSize, wrapperLeft, left);

    this.isPinPositionRight = left - +this.tooltipWidth <= wrapperLeft;

    this.x = DrawerTextUtil.standarizeXPosition(wrapperWidth, this.elementRef.nativeElement, this.textContent.nativeElement, this.x);
    this.y = DrawerTextUtil.standardizeYPosition(wrapperHeight, this.elementRef.nativeElement, this.textContent.nativeElement, this.y);
    this.changeDetector.markForCheck();
  }

  protected toggleActiveBox(isActive: boolean): void {
    this.isBoxActive = isActive;
    this.isActive.emit(isActive);
  }

  protected emitNoteChanges(): void {
    if (!this.textColor) {
      throw new Error('Missing text color');
    }

    const note: SingleNote = {
      distanceFromLeft: parseFloat(this.x),
      distanceFromTop: parseFloat(this.y),
      content: this.content,
      fontSize: this.fontSize,
      fontColor: this.textColor,
    };

    const noteToSave = {
      id: this.noteId,
      note,
    };

    this.noteStatus === NoteStatus.ReadyForSave
      ? this.saveNote.emit(noteToSave)
      : this.updateNote.emit(noteToSave);
  }
}
